/**
 * Debounced background autosave of an intake screen's serialized state to the
 * server, so a half-finished request survives a tab close. Creates the draft on
 * the first meaningful change (storing its id via setDraftId), then updates it
 * in place. `signature` is a cheap string the screen recomputes when its state
 * changes; `build()` produces the heavier snapshot only when a save fires.
 *
 * Returns a small status the screen can surface as a compact "Saving… / Saved"
 * hint so the user can trust their work is being kept.
 */
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ProjectDraftHints, ProjectDraftSource } from '@vari-crew/schemas';
import { createDraft, updateDraft, PROJECT_DRAFTS_KEY } from '@/api/project-drafts';

export interface DraftSnapshot {
  payload: Record<string, unknown>;
  hints: ProjectDraftHints;
  /** Skip saving until there's something worth keeping. */
  hasContent: boolean;
}

export type DraftSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export interface DraftAutosaveState {
  status: DraftSaveStatus;
  /** epoch ms of the last successful save, or null. */
  savedAt: number | null;
}

export function useDraftAutosave(opts: {
  source: ProjectDraftSource;
  draftId: string | null;
  setDraftId: (id: string) => void;
  signature: string;
  build: () => DraftSnapshot;
  /** Pause autosave (e.g. while the project is being submitted/deleted). */
  paused?: boolean;
}): DraftAutosaveState {
  const { source, signature, paused = false } = opts;
  const qc = useQueryClient();

  const [state, setState] = useState<DraftAutosaveState>({ status: 'idle', savedAt: null });

  // Latest values for the debounced + unmount-flush closures.
  const buildRef = useRef(opts.build);
  buildRef.current = opts.build;
  const draftIdRef = useRef(opts.draftId);
  draftIdRef.current = opts.draftId;
  const setDraftIdRef = useRef(opts.setDraftId);
  setDraftIdRef.current = opts.setDraftId;
  const signatureRef = useRef(signature);
  signatureRef.current = signature;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const savingRef = useRef(false);
  const lastSavedSig = useRef<string | null>(null);
  // Avoid setState after the screen unmounts (the unmount-flush save).
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const safeSet = (updater: (prev: DraftAutosaveState) => DraftAutosaveState) => {
    if (mountedRef.current) setState(updater);
  };

  async function save(): Promise<void> {
    if (savingRef.current || pausedRef.current) return;
    const snap = buildRef.current();
    if (!snap.hasContent) return;
    const sig = signatureRef.current;
    savingRef.current = true;
    safeSet((prev) => ({ status: 'saving', savedAt: prev.savedAt }));
    try {
      const id = draftIdRef.current;
      if (id) {
        await updateDraft(id, { payload: snap.payload, hints: snap.hints });
      } else {
        const created = await createDraft({ source, payload: snap.payload, hints: snap.hints });
        draftIdRef.current = created.id;
        setDraftIdRef.current(created.id);
      }
      lastSavedSig.current = sig;
      safeSet(() => ({ status: 'saved', savedAt: Date.now() }));
      void qc.invalidateQueries({ queryKey: PROJECT_DRAFTS_KEY });
    } catch {
      // Transient autosave failure — the next change retries.
      safeSet((prev) => ({ status: 'error', savedAt: prev.savedAt }));
    } finally {
      savingRef.current = false;
    }
  }

  // Debounced save whenever the state signature changes.
  useEffect(() => {
    if (paused || signature === lastSavedSig.current) return;
    const t = setTimeout(() => void save(), 1500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, paused]);

  // Flush any unsaved change when the screen unmounts (navigating away).
  useEffect(() => {
    return () => {
      if (!pausedRef.current && signatureRef.current !== lastSavedSig.current) {
        void save();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return state;
}
